import { useEffect, useState, useContext } from 'react'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import PropTypes from 'prop-types'

// Context
import { UserContext } from '../../context/UserContext'

// Internal components
import AccountBadge from '../AccountBadge/AccountBadge'
import PropertyBlock from '../PropertyBlock/PropertyBlock'
import Button from '../Button/Button'
import DetailBlock from '../DetailBlock/DetailBlock'
import ExerciseBlock from '../ExerciseBlock/ExerciseBlock'
import MaterialBlock from '../MaterialBlock/MaterialBlock'

// Utils
import getTrainingMaterialData from '../../utils/data/getTrainingMaterialData'
import getTrainingPropertyData from '../../utils/data/getTrainingPropertyData'
import getTrainingExerciseListData from '../../utils/data/getTrainingExerciseListData'
import getTrainingDurationData from '../../utils/data/getTrainingDurationData'
import getCombinedExercisesData from '../../utils/data/getCombinedExercisesData'

const TrainingBlock = (props) => {
  const { entry, index, frequency, purposes } = props

  const userContext = useContext(UserContext)

  // Translations
  const { t } = useTranslation(
    userContext?.userSession?.sportType?.slug ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_NAMESPACE ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_SPORT
  )

  const [exercisesState, setExercisesState] = useState([])

  const propertyData = getTrainingPropertyData(entry, t, 'trainingBlock')
  const exerciseData = getTrainingExerciseListData(entry)
  const durationsData = getTrainingDurationData(entry)
  const purposesData = purposes[0] ? purposes[0].data : false
  const materialData = getTrainingMaterialData(entry, t)

  const iteration = Math.ceil(parseInt(index + 1) / frequency)
  const objective = entry?.objectives?.filter((objective) => {
    return objective.level === 1
  })
  const url = `/training/id/${entry.id}`
  const title = entry.trainingTitle
  const userCreated = entry?.userGenerated

  useEffect(() => {
    setExercisesState(getCombinedExercisesData(entry))
  }, [entry])

  return (
    <div className="TrainingBlock">
      <div className="TrainingBlock-inner">
        <div className="TrainingBlock-main">
          <div className="TrainingBlock-content">
            {userCreated && (
              <AccountBadge initials={userContext.userInitials} modifiers={['AccountBadge--trainingBlock']} />
            )}
            {(iteration || objective) && (
              <p className="TrainingBlock-description">
                {iteration && `${t('DATE_WEEK')} ${iteration}`}
                {objective && ` - ${objective[0]?.title}`}
              </p>
            )}
            {url && (
              <h2 className="TrainingBlock-title">
                <Link href={url}>
                  <a className="TrainingBlock-link">{title}</a>
                </Link>
              </h2>
            )}
            {propertyData && (
              <div className="TrainingBlock-propertyBlock">
                <PropertyBlock data={propertyData} modifiers={['ProperyBlock--trainingBlock']} />
              </div>
            )}
            {exerciseData && (
              <div className="TrainingBlock-trainingList">
                <DetailBlock data={exerciseData} />
              </div>
            )}
            {url && (
              <Button
                link={{ href: url }}
                label={t('TRAININGBLOCK__BTN_WATCHDETAILS')}
                modifiers={['Button--exerciseBlockDetail']}
              />
            )}
            {materialData && (
              <div className="TrainingBlock-printOnly--materials" aria-hidden="true">
                <DetailBlock content={`<h3>${t('TRAININGBLOCK__REQUIREMENTS')}</h3>`} />
                <MaterialBlock data={materialData} modifiers={['MaterialBlock--trainingBlock']} />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="TrainingBlock-printOnly--exercises" aria-hidden="true">
        {(entry?.exercises?.length > 0 || entry?.warmingUpExercise?.length > 0) &&
          exercisesState.map((exercise, index) => {
            const purpose = purposesData
              ? purposesData.filter((purpose) => {
                  return purpose.id === parseInt(exercise.id)
                })
              : false
            return (
              <ExerciseBlock
                entry={exercise}
                purpose={purpose?.[0]?.data}
                duration={durationsData[exercise.id]}
                index={index}
                key={`ExerciseBlock--${index}`}
              />
            )
          })}
      </div>
    </div>
  )
}

TrainingBlock.propTypes = {
  entry: PropTypes.object.isRequired,
  index: PropTypes.number,
  frequency: PropTypes.number,
  purposes: PropTypes.any
}

export default TrainingBlock
